import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatRelative } from '../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import './HeaderNotifications.css';

export type NotificationScope = 'mine' | 'team' | 'shared';

export interface HeaderNotificationItem {
  id: string;
  title: string;
  subtitle?: string;
  scope: NotificationScope;
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  createdAt?: string;
  isUnread: boolean;
  deepLink?: string;
  sourceType?: string;
}

interface HeaderNotificationsProps {
  items: HeaderNotificationItem[];
  onMarkRead?: (id: string) => void | Promise<void>;
  onClear?: (id: string) => void | Promise<void>;
  onClearAllVisible?: (scope: 'all' | NotificationScope) => void | Promise<void>;
}

const formatRelativeTime = (value?: string): string => {
  return value ? formatRelative(value) : '';
};

const scopeLabel = (scope: NotificationScope): string => {
  if (scope === 'mine') return 'Mine';
  if (scope === 'team') return 'Team';
  return 'Shared';
};

const severityClass = (severity?: HeaderNotificationItem['severity']): string => {
  return `severity-${severity || 'info'}`;
};

export const HeaderNotifications: React.FC<HeaderNotificationsProps> = ({
  items,
  onMarkRead,
  onClear,
  onClearAllVisible,
}) => {
  useDateTimePreferences();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'all' | NotificationScope>('all');
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string>('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  const runAction = async (actionKey: string, action: () => void | Promise<void>, fallbackMessage: string) => {
    setActionError('');
    setPendingActionKey(actionKey);
    try {
      await action();
    } catch (err) {
      setActionError(fallbackMessage);
      console.error(fallbackMessage, err);
    } finally {
      setPendingActionKey((current) => (current === actionKey ? null : current));
    }
  };

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (event.target instanceof Node && rootRef.current.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const unreadCount = useMemo(() => items.filter((x) => x.isUnread).length, [items]);

  const visible = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const aTs = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTs = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTs - aTs;
    });
    if (scope === 'all') return sorted;
    return sorted.filter((x) => x.scope === scope);
  }, [items, scope]);

  return (
    <div className="header-notifications" ref={rootRef}>
      <button
        type="button"
        className="notif-trigger"
        aria-label="Open notifications"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <span className="notif-bell" aria-hidden="true">&#128276;</span>
        {unreadCount > 0 ? <span className="notif-chip">{Math.min(unreadCount, 99)}</span> : null}
      </button>

      {open ? (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-panel-header">
            <strong>Notifications</strong>
            {visible.length > 0 ? (
              <button
                type="button"
                className="notif-clear-all"
                disabled={Boolean(pendingActionKey)}
                onClick={() => {
                  if (!onClearAllVisible) return;
                  void runAction(
                    `clear-all:${scope}`,
                    () => onClearAllVisible(scope),
                    'Failed to clear notifications. Please retry.'
                  );
                }}
              >
                Clear visible
              </button>
            ) : null}
          </div>

          {actionError ? <div className="notif-action-error">{actionError}</div> : null}

          <div className="notif-filters" role="tablist" aria-label="Notification scope filters">
            {(['all', 'mine', 'team', 'shared'] as const).map((key) => (
              <button
                key={key}
                type="button"
                className={`notif-filter ${scope === key ? 'active' : ''}`}
                onClick={() => setScope(key)}
              >
                {key === 'all' ? 'All' : scopeLabel(key)}
              </button>
            ))}
          </div>

          <div className="notif-list">
            {visible.length === 0 ? (
              <div className="notif-empty">No notifications</div>
            ) : (
              visible.map((item) => (
                <div key={item.id} className={`notif-item ${item.isUnread ? 'unread' : ''}`}>
                  <div className="notif-item-main">
                    <div className="notif-item-title-row">
                      <span className="notif-item-title">{item.title}</span>
                      <span className={`notif-severity ${severityClass(item.severity)}`}>{scopeLabel(item.scope)}</span>
                    </div>
                    {item.subtitle ? <div className="notif-item-subtitle">{item.subtitle}</div> : null}
                    <div className="notif-item-time">{formatRelativeTime(item.createdAt)}</div>
                  </div>
                  <div className="notif-item-actions">
                    {item.deepLink?.startsWith('/') && !/^\/[\\/\s]/.test(item.deepLink) ? (
                      <a href={item.deepLink}>{item.sourceType === 'schema-remediation' ? 'Resolve schema issue' : 'Open'}</a>
                    ) : item.sourceType === 'schema-remediation' ? (
                      <span role="alert">Resolution link unavailable. Reload notifications or open the integration receipts.</span>
                    ) : null}
                    {item.isUnread ? (
                      <button
                        type="button"
                        disabled={Boolean(pendingActionKey)}
                        onClick={() => {
                          if (!onMarkRead) return;
                          void runAction(
                            `read:${item.id}`,
                            () => onMarkRead(item.id),
                            'Failed to mark as read. Please retry.'
                          );
                        }}
                      >
                        Read
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={Boolean(pendingActionKey)}
                      onClick={() => {
                        if (!onClear) return;
                        void runAction(
                          `clear:${item.id}`,
                          () => onClear(item.id),
                          'Failed to clear notification. Please retry.'
                        );
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
